import type { Layer, LayerOverrides } from '@/types/layers/layer';

export type RefKey = string;
export type RefLayerDefinition = Omit<Layer, 'id' | 'parentId'>;

export interface CreativeElementLayerData {
  refTree: Record<RefKey, RefLayerDefinition>;
  childOrder: RefKey[];
}

export interface SizeConfiguration {
  width: number;
  height: number;
  adId: string;
  overrides: Record<RefKey, Partial<LayerOverrides>>;
}

export interface CreativeElement {
  id: number;
  name: string;
  tag: number;
  layerData: CreativeElementLayerData;
  sizeConfigurations: SizeConfiguration[];
  status: number;
  isDeletedComponent: boolean;
  thumbnailUrl: string;
  version: number;
}

export enum CreativeElementStatus {
  None = 0,
  Active = 1,
  Deleted = 2
}

export interface CreativeElementSaveRequest {
  name: string;
  tag: number;
  liteLayerData: string;
  sizeConfigurations: string;
  imageFile: string;
}

export interface CreativeElementApiRequest {
  name: string;
  tagId: number;
  liteLayerData: string;
  sizeConfigurations: string;
  imageFile: string;
}

export interface CreativeElementTagResponse {
  id: number;
  name: string;
  thumbnailUrl: string;
  isDefault: boolean;
}

export interface CreativeElementResponse {
  id: number;
  name: string;
  tag: CreativeElementTagResponse | null;
  layerData?: string | null;
  liteLayerData?: string | null;
  sizeConfigurations: string;
  thumbnailUrl: string;
  status: CreativeElementStatus;
  version: number;
  createdBy?: number;
  updatedBy?: number;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Lightweight row from the meta endpoint — no layer data.
 */
export interface CreativeElementMeta {
  id: number;
  version: number;
  status: CreativeElementStatus;
  name: string;
  thumbnailUrl: string;
}

/**
 * What the component-version prompt is asking about.
 *
 * Built by the load-time reconcile and handed to the dialog through the store's
 * await-bridge.
 */
export interface VersionPrompt {
  componentId: number;
  name: string;
  thumbnailUrl: string;
  fromVersion: number | null;
  toVersion: number;
  /** How many containers in this template use the component. */
  instanceCount: number;
}

/** Paginated list response for the Elements panel. */
export interface CreativeElementListResponse {
  items: CreativeElement[];
  totalCount: number;
}

/** One page of raw tag rows, with the total number of tags on the BP. */
export interface CreativeElementTagListResponse {
  items: CreativeElementTagResponse[];
  totalCount: number;
}

export interface CreativeElementCategory {
  id: number;
  name: string;
  thumbnailUrl: string;
  isDefault: boolean;
}

export interface CreateCategoryRequest {
  name: string;
  /** Base64 PNG of the emoji grid; the server uploads it and returns the URL. */
  imageFile: string;
}

export interface ListComponentsParams {
  tag?: number;
  pageNumber: number;
  pageSize: number;
  /** Matched against element name OR tag name, server-side. */
  search?: string;
}

export interface ListCategoriesParams {
  pageNumber: number;
  pageSize: number;
}
